// src/slices/claimDossier/thunk.ts
// Async thunks cho Thẩm định Hồ sơ Trả thưởng — toast xử lý NGAY TRONG THUNK, theo đúng pattern đã ổn định
// ở prizeStructure/drawResult (xem implementation-context-fe.md mục 3.4).

import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import {
  getClaimDossiersApi,
  getClaimDossierByIdApi,
  createClaimDossierApi,
  approveClaimDossierApi,
  cancelClaimDossierApi,
  payClaimDossierApi,
} from 'helpers/fakebackend_helper';
import { CreateClaimDossierPayload } from 'pages/ClaimDossier/types';

// api_helper.ts::APIClient.get() nối thẳng `key=${params[key]}` vào query string kể cả khi value là
// undefined (thành chuỗi "Status=undefined" literal) — phải lọc bỏ key null/undefined/'' trước khi gọi API,
// theo đúng pattern cleanParams() đã dùng ở slices/sinhBoSoQr, ticketType, ticketDesignTemplate.
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// FR-001/FR-002: danh sách hồ sơ (phân trang, lọc theo trạng thái/CCCD/số vé) — PaymentStatus dùng cho màn Kế
// toán Chi trả lọc theo trạng thái chi (ChuaChi/DaChi).
export const fetchClaimDossiers = createAsyncThunk(
  'ClaimDossier/fetchList',
  async (
    params: {
      PageIndex?: number;
      PageSize?: number;
      Status?: string;
      PaymentStatus?: string;
      ClaimantIdNumber?: string;
      TicketNumber?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await getClaimDossiersApi(cleanParams(params));
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể tải danh sách hồ sơ';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-007: xem chi tiết 1 hồ sơ
export const fetchClaimDossierById = createAsyncThunk(
  'ClaimDossier/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getClaimDossierByIdApi(id);
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể tải chi tiết hồ sơ';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-003/FR-004: tạo hồ sơ mới — server tự chấm vé + tự tính Thuế TNCN
export const createClaimDossier = createAsyncThunk(
  'ClaimDossier/create',
  async (data: CreateClaimDossierPayload, { rejectWithValue }) => {
    try {
      const response = await createClaimDossierApi(data);
      toast.success('Tạo hồ sơ thành công, đang chờ duyệt.');
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể tạo hồ sơ';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-005: ký duyệt hồ sơ
export const approveClaimDossier = createAsyncThunk(
  'ClaimDossier/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approveClaimDossierApi(id);
      toast.success('Ký duyệt hồ sơ thành công.');
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể ký duyệt hồ sơ';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// FR-006: hủy hồ sơ
export const cancelClaimDossier = createAsyncThunk(
  'ClaimDossier/cancel',
  async ({ id, reason }: { id: number; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await cancelClaimDossierApi(id, reason);
      toast.success('Đã hủy hồ sơ.');
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể hủy hồ sơ';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Màn Kế toán Chi trả — thao tác "Chuyển khoản": ChuaChi -> DaChi
export const payClaimDossier = createAsyncThunk(
  'ClaimDossier/pay',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await payClaimDossierApi(id);
      toast.success('Đã chuyển khoản chi trả.');
      return response as any;
    } catch (error: any) {
      const message = error?.data?.message || error?.message || 'Không thể chuyển khoản chi trả';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);
